import { FAQPageJsonLd } from 'next-seo';
import React from 'react';

import ExpandablePanel from '../../components/interactive/ExpandablePanel';

export type FAQItem = {
  question: string;
  answer: string;
};

const DEFAULT_FAQS: FAQItem[] = [
  {
    question: 'How do I integrate Gaspar with my Single Sign-On (SSO) provider?',
    answer:
      'Follow our step-by-step guides for seamless integration with popular SSO providers like Google Workspace and Office 365. Click here for detailed instructions.',
  },
  {
    question: 'Can I connect Gaspar with multiple messaging platforms simultaneously?',
    answer:
      'Yes, Gaspar supports integration with multiple messaging platforms. Configure your preferences effortlessly to communicate seamlessly across different channels.',
  },
  {
    question: "What ITSM tools are compatible with Gaspar's Admin Portal?",
    answer:
      'Gaspar seamlessly integrates with various ITSM tools, including Jira, ServiceNow, Freshservice, and Asana. Choose the one that aligns with your workflow.',
  },
];

type FAQWidgetProps = {
  items?: FAQItem[];
  description?: string;
};

const FAQWidget: React.FC<FAQWidgetProps> = ({ items = DEFAULT_FAQS, description }) => {
  return (
    <div className="flex flex-col mb-6">
      {/* Question and answer pairs are the format answer engines lift most readily, so the same
          items that render the panels below are also published as FAQPage structured data. Each
          page mounts this widget at most once, which keeps a single FAQPage node per page. */}
      <FAQPageJsonLd mainEntity={items.map((item) => ({ questionName: item.question, acceptedAnswerText: item.answer }))} />
      {/* h2, not h1: the page h1 comes from HeaderSection, and ExpandablePanel renders each
          question as an h3, so the FAQ reads as a proper h1 > h2 > h3 outline. */}
      <h2 className="text-section-title mb-8">FAQ</h2>
      {description && <p className="text-[32px] md:text-h3 mb-6">{description}</p>}
      <FAQPanel items={items} />
    </div>
  );
};

FAQWidget.displayName = 'FAQWidget';
export default FAQWidget;

/************************************************************************************************
 * Helpers
 ************************************************************************************************/

type FAQPanelProps = {
  items: FAQItem[];
};

const FAQPanel: React.FC<FAQPanelProps> = React.memo(({ items }) => {
  return (
    <div className="flex flex-col divide-y divide-purple/50">
      {items.map((item, index) => (
        <ExpandablePanel key={index} title={item.question} titleClassName="text-base md:text-xl font-semibold pr-4">
          <p className="pb-4 text-pureWhite/70 leading-relaxed">{item.answer}</p>
        </ExpandablePanel>
      ))}
    </div>
  );
});

FAQPanel.displayName = 'FAQPanel';
